/**
 * Everything that is currently applied, in one list.
 *
 * The list is derived from the recipe by `activeControls`, never recorded as
 * the user works, so it stays true for changes that arrive from a preset, a
 * synced photo or an accepted proposal. Each row carries its own reset, which
 * is the only place in the app where a single decision can be taken back
 * without disturbing the ones around it.
 */

import { useCallback, useMemo, type CSSProperties } from 'react';
import { activeControls } from '@lumos/core';
import { useSelectedPhoto, useStore, type PanelId } from '../../state/store.ts';
import { Panel } from '../controls/Panel.tsx';

const LIST: CSSProperties = { listStyle: 'none', margin: 0, padding: 0 };

/** Name and value together are one target, so the row is easy to hit. */
const JUMP: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  textAlign: 'left',
};

const PANEL_IDS: readonly PanelId[] = [
  'edits',
  'looks',
  'light',
  'color',
  'curve',
  'detail',
  'effects',
  'geometry',
  'masks',
  'history',
];

/** `ActiveControl.panel` is a plain string in core, which knows nothing of the rail. */
function asPanelId(value: string): PanelId | null {
  return PANEL_IDS.find((id) => id === value) ?? null;
}

function reveal(panel: PanelId): void {
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // The body is only in the DOM once the panel is open, so the scroll waits a frame.
  window.requestAnimationFrame(() => {
    const body = document.getElementById(`panel-${panel}`);
    const section = body?.closest('.panel') ?? body;
    section?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
  });
}

export function EditsPanel() {
  const photo = useSelectedPhoto();
  const setPanelOpen = useStore((state) => state.setPanelOpen);
  const resetControl = useStore((state) => state.resetControl);

  const recipe = photo?.recipe;
  const asShot = photo?.image?.asShot;
  const entries = useMemo(
    () =>
      recipe
        ? activeControls(recipe, asShot ? { kelvin: asShot.kelvin, tint: asShot.tint } : undefined)
        : [],
    [recipe, asShot],
  );

  const open = useCallback(
    (panel: string) => {
      const id = asPanelId(panel);
      if (!id) return;
      setPanelOpen(id, true);
      reveal(id);
    },
    [setPanelOpen],
  );

  if (!photo) return null;

  return (
    <Panel id="edits" title={entries.length > 0 ? `Edits · ${entries.length}` : 'Edits'}>
      {entries.length === 0 ? (
        <p className="edits-empty">Nothing changed yet.</p>
      ) : (
        <ul className="edits" style={LIST}>
          {entries.map((entry) => (
            <li className="edit-row" key={entry.id}>
              <button
                type="button"
                style={JUMP}
                onClick={() => open(entry.panel)}
                aria-label={`Show ${entry.label}, currently ${entry.value}`}
              >
                <span className="edit-name">{entry.label}</span>
                <span className="edit-value">{entry.value}</span>
              </button>
              <button
                type="button"
                className="edit-undo"
                onClick={() => resetControl(entry.id)}
                aria-label={`Reset ${entry.label}`}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
